// 시간표 뷰 (#calTimeGrid, spec docs/superpowers/specs/2026-09-19-calendar-timegrid-design.md §4.2)
// — 처음 스크롤·리프레시 뒤 스크롤 보존, 빈 시간 끌어 새 일정(15분 스냅), 블록 끌어 이동(세로 시각·가로 날짜),
// 아래 손잡이로 끝 시각, 반복 인스턴스의 범위 확인(appChoose), 모바일 3일/1일 열.
// 백로그 BB 라운드(2026-09-25):
//   BB8 포인터 이벤트 — 마우스·펜은 곧바로, 터치는 길게 눌러(350ms) 끌기를 켠다(그 전에 움직이면 스크롤);
//       켜진 뒤의 touchmove 는 막아 스크롤·기간 넘기기 제스처가 끼어들지 못하게 한다.
//   BB4 종일 띠 막대 — 끌어 날짜 옮기기 · 오른쪽 손잡이로 끝 날짜(월 그리드와 같은 patch).
//   BB3 키보드 — 블록에서 ↑↓ 15분 · ←→ 하루 · Shift+↑↓ 끝 시각, 멈추면(0.7초·포커스를 떠나면) 한 번 저장,
//       Esc 되돌림, 저장 뒤 같은 블록으로 포커스가 돌아온다.
//   BB5 「+N」 — 세 열을 넘는 겹침의 나머지를 목록으로 연다.
//   BB9 본문 높이 — 화면에 맞춰(ResizeObserver) 잰다; 짧은 블록 모양은 서버의 short/tiny 클래스.
//   XA20 터치로 빈 칸을 짧게 톡 — 패널을 바로 열지 않고 그 30분 눈금에 1시간 「＋ 새 일정」 유령 블록을 놓는다;
//       유령을 톡 하면 그 날짜·시각의 새 일정 패널, 다른 곳을 누르거나 스크롤·Esc·새로고침이면 사라진다.
//       마우스·펜 클릭과 길게 눌러 끌기는 그대로.
// 저장은 기존 patch(start_time/end_time/start_date/end_date) 로 실행 취소 스택에 들어간다.
import {
    api, patchCommand, commit, showToast, labels, fmtCount, refreshGrid, beginDrag, endDrag,
} from './calendar-history.js';
import { appChoose } from './confirm-dialog.js';
import { openExclusive, closedExclusive } from './popover-registry.js';

const SNAP = 15; // 분
const DAY = 24 * 60;
const LONG_PRESS = 350; // 터치: 이만큼 눌러야 끌기가 켜진다 (BB8)
const TOUCH_SLOP = 8;
const KB_IDLE = 700; // 키보드 이동이 멈추고 이만큼 지나면 저장 (BB3)
const MORE_ID = 'cal-tg-more';
const pad = (n) => String(n).padStart(2, '0');
const minToHHMM = (m) => { m = Math.max(0, Math.min(DAY, m)); return pad(Math.floor(m / 60)) + ':' + pad(m % 60); };
const hhmmToMin = (s) => { const m = /^(\d{1,2}):(\d{2})$/.exec(s || ''); return m ? (+m[1]) * 60 + (+m[2]) : NaN; };
const snap = (m) => Math.round(m / SNAP) * SNAP;
function addDays(iso, n) {
    const d = new Date(iso + 'T00:00:00Z');
    if (isNaN(d.getTime())) return iso;
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
function daysBetween(a, b) {
    const x = new Date(a + 'T00:00:00Z'), y = new Date(b + 'T00:00:00Z');
    return Math.round((y - x) / 86400000);
}

// 모듈 상태: 루트는 리프레시마다 새 노드라 루트 리스너는 매번, 문서 리스너는 한 번만
const state = { root: null, body: null, pxMin: 0.8, drag: null, pending: null, swallowClick: false, scrollTop: null, first: null, docBound: false, kb: null, focusId: '', ghost: null, ghostTap: null };

// 남의 개인 일정은 보기만 (패널과 같은 규칙)
const editable = (el) => !(el.dataset.isowner !== '1' && el.dataset.calid === '');

export function setupCalendarTimeGrid() {
    const root = document.getElementById('calTimeGrid');
    // 리프레시의 copyAttrs 가 fresh 에 없는 data-* 를 지우므로 묶음 표시는 JS 속성으로 (data-tg-bound 는 테스트용 마커)
    if (!root || root.__tgBound) return;
    root.__tgBound = true;
    state.drag = null;
    state.pending = null;
    state.first = null;
    hydrate(root, true);

    // 끈 뒤의 click 은 삼킨다 — 캡처 단계라 막대의 클릭(패널 열기)보다 먼저다
    root.addEventListener('click', (e) => {
        if (state.swallowClick) { state.swallowClick = false; e.preventDefault(); e.stopPropagation(); }
    }, true);
    root.addEventListener('click', (e) => {
        const shift = e.target.closest('[data-tg-shift]');
        if (shift) {
            state.first = (state.first || 0) + (parseInt(shift.dataset.tgShift, 10) || 0) * visibleCols();
            applyCols(root, false);
            return;
        }
        const moreBtn = e.target.closest('.cal-tg-more-btn');
        if (moreBtn) { e.stopPropagation(); toggleMore(moreBtn); return; }
        const ghost = e.target.closest('.cal-tg-ghost');
        if (ghost) {
            e.stopPropagation();
            // 유령을 놓은 그 톡의 click(같은 포인터, 또는 곧바로 온 것)은 유령 위에 떨어져도 패널을 열지 않는다
            const t = state.ghostTap;
            state.ghostTap = null;
            if (t && ((typeof e.pointerId === 'number' && e.pointerId > 0 && e.pointerId === t.id) || performance.now() - t.at < 350)) return;
            openFromGhost(ghost);
            return;
        }
        const item = e.target.closest('.cal-tg-more-item');
        if (item) { e.stopPropagation(); closeMore(); if (window.openCalendarEvent) window.openCalendarEvent(item); return; }
        const block = e.target.closest('.cal-tg-block, .cal-tg-bar');
        if (block && window.openCalendarEvent) { e.stopPropagation(); window.openCalendarEvent(block); }
    });
    root.addEventListener('dblclick', (e) => {
        const block = e.target.closest('.cal-tg-block, .cal-tg-bar');
        if (block && window.openCalendarEvent) { e.preventDefault(); window.openCalendarEvent(block, { edit: true }); }
    });
    root.addEventListener('pointerdown', onDown);
    root.addEventListener('keydown', onKey);
    // 포커스를 떠나면 기다리지 않고 저장 — 다만 ←→ 가 블록을 옆 열로 옮기며 잠깐 잃은 포커스는 아니다(다음 틱에 다시 본다)
    root.addEventListener('focusout', (e) => {
        const kb = state.kb;
        if (!kb || e.target !== kb.block) return;
        setTimeout(() => { if (state.kb === kb && document.activeElement !== kb.block) flushKb(); }, 0);
    });
    // 터치 끌기가 켜진 동안: 스크롤·기간 넘기기(touch-gestures.js 의 문서 리스너)를 막는다
    root.addEventListener('touchmove', (e) => {
        if (state.drag && state.drag.touch) { e.preventDefault(); e.stopPropagation(); }
    }, { passive: false });
    root.addEventListener('touchend', (e) => { if (state.touchDragEnded) { state.touchDragEnded = false; e.stopPropagation(); } });
    // 길게 누르기의 컨텍스트 메뉴(안드로이드)는 끌기와 겹치므로 누르고 있는 동안에는 막는다
    root.addEventListener('contextmenu', (e) => { if (state.pending || (state.drag && state.drag.touch)) { e.preventDefault(); e.stopPropagation(); } });

    if (!state.docBound) {
        state.docBound = true;
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onCancel);
        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            if (state.drag) cancelDrag();
            else if (state.ghost) removeGhost();
        });
        // 유령 밖을 누르면(다른 칸·머리·스크롤의 시작) 유령은 사라진다 — 캡처라 루트의 onDown 보다 먼저 (XA20)
        document.addEventListener('pointerdown', (e) => {
            if (state.ghost && !(e.target.closest && e.target.closest('.cal-tg-ghost'))) removeGhost();
        }, true);
        window.addEventListener('resize', () => { if (state.root && state.root.isConnected) { applyCols(state.root, false); fitBody(); } });
        if (typeof ResizeObserver !== 'undefined') {
            // 사이드바 접기·창 크기처럼 본문 위치가 바뀌면 높이를 다시 잰다 (BB9)
            new ResizeObserver(() => { if (state.root && state.root.isConnected) fitBody(); }).observe(document.documentElement);
        }
        // 리프레시는 루트 노드를 두고 innerHTML 만 갈아끼운다(리스너 유지) — 본문·스크롤·열 상태는 다시 잡는다
        document.addEventListener('calendar:refreshed', () => {
            const r = document.getElementById('calTimeGrid');
            if (r) hydrate(r, false);
        });
    }
}

// 본문(.cal-tg-body)은 리프레시마다 새 노드다: 스크롤 위치(처음엔 ScrollHour, 그 뒤엔 마지막 위치)와 모바일 열을 되살린다.
function hydrate(root, initial) {
    const body = root.querySelector('.cal-tg-body');
    if (!body) return;
    const pxHour = parseInt(root.dataset.pxHour, 10) || 48;
    state.root = root;
    state.body = body;
    state.pxMin = pxHour / 60;
    state.kb = null;
    state.ghost = null; // 새로 그린 열에는 유령이 없다 (XA20)
    state.ghostTap = null;
    root.dataset.tgBound = '1';
    fitBody();
    body.scrollTop = state.scrollTop != null ? state.scrollTop : (parseInt(root.dataset.scrollHour, 10) || 8) * pxHour;
    if (!body.dataset.tgScroll) {
        body.dataset.tgScroll = '1';
        body.addEventListener('scroll', () => { state.scrollTop = body.scrollTop; if (state.ghost) removeGhost(); }, { passive: true });
    }
    applyCols(root, initial);
    // 키보드로 옮긴 블록: 저장 뒤 새로 그려진 같은 블록으로 포커스를 돌려준다 (BB3)
    if (state.focusId) {
        const again = root.querySelector('.cal-tg-block[data-id="' + CSS.escape(state.focusId) + '"]');
        state.focusId = '';
        if (again) again.focus({ preventScroll: true });
    }
}

// ── 본문 높이 (BB9): 화면 아래까지 — 고정 식(100vh - 250px) 대신 실제 위치로 잰다 ──
function scrollerOf(el) {
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if (oy === 'auto' || oy === 'scroll') return p;
    }
    return null;
}
function fitBody() {
    const root = state.root, body = state.body;
    if (!root || !body || !body.isConnected) return;
    const sc = scrollerOf(root);
    // 위치는 "맨 위로 스크롤했을 때" 기준 — 페이지를 내려도 높이가 출렁이지 않게
    const scroll = sc ? sc.scrollTop : window.scrollY;
    const viewTop = sc ? sc.getBoundingClientRect().top : 0;
    const viewH = sc ? sc.clientHeight : window.innerHeight;
    const bodyTop = body.getBoundingClientRect().top + scroll - viewTop;
    const hint = root.nextElementSibling && root.nextElementSibling.classList.contains('cal-grid-hint') ? root.nextElementSibling : null;
    const tabbar = document.getElementById('mTabbar');
    const reserve = 16 + (hint ? hint.getBoundingClientRect().height + 8 : 0) + (tabbar && tabbar.offsetParent !== null ? tabbar.offsetHeight : 0);
    root.style.setProperty('--tg-body-h', Math.max(240, Math.round(viewH - bodyTop - reserve)) + 'px');
}

// ── 모바일 열: ≤768px 3일, ≤480px 1일 — 나머지 열은 hidden, 그리드 트랙은 --cols ──
function visibleCols() { const w = window.innerWidth; return w <= 480 ? 1 : (w <= 768 ? 3 : 7); }
function applyCols(root, initial) {
    const n = visibleCols();
    let first = state.first;
    if (initial || first == null) {
        // 처음: 오늘이 이 주면 오늘부터, 아니면 일요일부터 (리프레시는 state.first 를 지킨다 — data-first 는 fresh 의 0 으로 되돌아온다)
        const today = root.querySelector('.cal-tg-dayhead.today');
        first = today ? parseInt(today.dataset.col, 10) || 0 : 0;
    }
    first = Math.max(0, Math.min(first, 7 - n));
    state.first = first;
    root.dataset.first = String(first);
    root.style.setProperty('--cols', String(n));
    root.style.setProperty('--first', String(first));
    root.querySelectorAll('[data-col]').forEach((el) => {
        const c = parseInt(el.dataset.col, 10);
        el.hidden = c < first || c >= first + n;
    });
    root.querySelectorAll('.cal-tg-bar').forEach((b) => {
        const c = parseInt(b.style.getPropertyValue('--col'), 10) || 0;
        const s = parseInt(b.style.getPropertyValue('--span'), 10) || 1;
        b.hidden = c + s <= first || c >= first + n;
    });
    root.classList.toggle('cal-tg-narrow', n < 7);
    const prev = root.querySelector('[data-tg-shift="-1"]'), next = root.querySelector('[data-tg-shift="1"]');
    if (prev) prev.disabled = first <= 0;
    if (next) next.disabled = first + n >= 7;
}

// ── 끌기 ──
function colAt(x, y) {
    const el = document.elementFromPoint(x, y);
    return el ? el.closest('.cal-tg-col') : null;
}
function minAt(col, y) {
    const r = col.getBoundingClientRect();
    return (y - r.top) / state.pxMin;
}
function timeRangeOf(block) {
    const st = hhmmToMin(block.dataset.stime);
    let et = hhmmToMin(block.dataset.etime);
    if (isNaN(et) || et <= st) et = st + 30;
    return [st, Math.min(DAY, et)];
}
function dayWidth(root) {
    const cols = root.querySelector('.cal-tg-allday-cols');
    return cols ? cols.getBoundingClientRect().width / visibleCols() : 0;
}

// 누른 자리에서 끌기 상태를 만든다 (아직 시작 전 — moved 가 참이 될 때 보이기 시작한다)
function dragFor(e) {
    if (e.target.closest('.cal-tg-more')) return null;
    const bar = e.target.closest('.cal-tg-bar');
    if (bar) {
        if (!editable(bar)) return null;
        return { kind: e.target.closest('.cal-tg-bar-resize') ? 'band-resize' : 'band-move', bar, startX: e.clientX, startY: e.clientY,
            col0: parseInt(bar.dataset.col0, 10) || 0, span0: parseInt(bar.dataset.span0, 10) || 1, dayW: dayWidth(state.root), delta: 0, moved: false };
    }
    const col = e.target.closest('.cal-tg-col');
    if (!col) return null;
    const block = e.target.closest('.cal-tg-block');
    if (block) {
        if (!editable(block)) return null;
        const [st, et] = timeRangeOf(block);
        return {
            kind: e.target.closest('.cal-tg-resize') ? 'resize' : 'move',
            block, col, origCol: col, startX: e.clientX, startY: e.clientY,
            origSt: st, origEt: et, origDate: col.dataset.date, curSt: st, curEt: et, curDate: col.dataset.date,
            origTimeText: block.querySelector('.cal-tg-block-time') ? block.querySelector('.cal-tg-block-time').textContent : '',
            moved: false,
        };
    }
    const m = Math.max(0, Math.min(DAY - SNAP, Math.floor(minAt(col, e.clientY) / SNAP) * SNAP));
    return { kind: 'create', col, date: col.dataset.date, startX: e.clientX, startY: e.clientY, anchor: m, st: m, et: m + SNAP, moved: false };
}

function onDown(e) {
    if (e.button !== 0 || state.drag || state.pending) return;
    if (e.target.closest('.cal-tg-ghost')) return; // 유령은 click 이 연다
    if (state.kb) flushKb();
    const d = dragFor(e);
    if (!d) return;
    d.pointerId = e.pointerId;
    if (e.pointerType === 'touch') {
        // 터치: 길게 눌러야 끌기 — 그 전에 움직이면 스크롤이다 (BB8)
        d.touch = true;
        state.pending = { d, x: e.clientX, y: e.clientY, timer: setTimeout(() => armTouch(), LONG_PRESS) };
        return;
    }
    e.preventDefault();
    state.drag = d;
    beginDrag();
}
function armTouch() {
    const p = state.pending;
    if (!p) return;
    state.pending = null;
    state.drag = p.d;
    state.drag.armed = true;
    beginDrag();
    const el = p.d.block || p.d.bar;
    if (el) el.classList.add('dragging');
    else showSel(p.d.col, p.d.st, p.d.et);
    if (navigator.vibrate) { try { navigator.vibrate(15); } catch (_) { /* ignore */ } }
}

function onMove(e) {
    const p = state.pending;
    if (p && e.pointerId === p.d.pointerId) {
        if (Math.abs(e.clientX - p.x) > TOUCH_SLOP || Math.abs(e.clientY - p.y) > TOUCH_SLOP) { clearTimeout(p.timer); state.pending = null; }
        return;
    }
    const d = state.drag;
    if (!d || (d.pointerId != null && e.pointerId !== d.pointerId)) return;
    if (!d.moved && !d.armed && Math.abs(e.clientY - d.startY) < 3 && Math.abs(e.clientX - d.startX) < 3) return;
    d.moved = true;
    if (d.kind === 'band-move' || d.kind === 'band-resize') { moveBand(d, e); return; }
    if (d.kind === 'create') {
        const m = Math.max(0, Math.min(DAY, snap(minAt(d.col, e.clientY))));
        const lo = Math.min(d.anchor, m), hi = Math.max(d.anchor, m);
        d.st = Math.max(0, Math.min(DAY - SNAP, lo));
        d.et = Math.max(d.st + SNAP, Math.min(DAY, hi));
        showSel(d.col, d.st, d.et);
        return;
    }
    const b = d.block;
    b.classList.add('dragging');
    const dy = (e.clientY - d.startY) / state.pxMin;
    if (d.kind === 'move') {
        const dur = d.origEt - d.origSt;
        let st = snap(d.origSt + dy);
        st = Math.max(0, Math.min(DAY - dur, st));
        const col = colAt(e.clientX, e.clientY) || d.col;
        d.col = col;
        d.curSt = st; d.curEt = st + dur; d.curDate = col.dataset.date;
        b.style.setProperty('--top', String(st));
        if (b.parentElement !== col) col.appendChild(b); // 열 이동 미리보기
    } else {
        let et = snap(d.origEt + dy);
        et = Math.max(d.origSt + SNAP, Math.min(DAY, et));
        d.curEt = et;
        b.style.setProperty('--h', String(et - d.origSt));
    }
    const t = b.querySelector('.cal-tg-block-time');
    if (t) t.textContent = minToHHMM(d.curSt) + '–' + minToHHMM(d.curEt);
}

// 띠 막대 미리보기 (BB4): 날 단위로 --col(옮기기)·--span(끝 날짜)
function moveBand(d, e) {
    const b = d.bar;
    b.classList.add('dragging');
    let delta = d.dayW > 0 ? Math.round((e.clientX - d.startX) / d.dayW) : 0;
    if (d.kind === 'band-resize') {
        const days = daysBetween(b.dataset.start, b.dataset.end); // 끝은 시작보다 앞으로 가지 않는다
        delta = Math.max(-days, delta);
        b.style.setProperty('--span', String(Math.max(1, d.span0 + delta)));
    } else {
        b.style.setProperty('--col', String(d.col0 + delta));
    }
    d.delta = delta;
}

function onUp(e) {
    const p = state.pending;
    if (p && e.pointerId === p.d.pointerId) {
        // 짧게 누른 터치: 빈 칸이면 그 30분 눈금에 1시간 유령 블록 — 한 번 더 톡 해야 패널이 열린다 (XA20;
        // 블록·막대는 click 이 연다)
        clearTimeout(p.timer);
        state.pending = null;
        if (p.d.kind === 'create') {
            const st = p.d.anchor - (p.d.anchor % 30);
            showGhost(p.d.col, p.d.date, st, Math.min(DAY, st + 60));
            state.ghostTap = { id: e.pointerId, at: performance.now() };
        }
        return;
    }
    const d = state.drag;
    if (!d || (d.pointerId != null && e.pointerId !== d.pointerId)) return;
    state.drag = null;
    endDrag();
    hideSel();
    if (d.touch) state.touchDragEnded = true;
    if (d.kind === 'band-move' || d.kind === 'band-resize') { finishBand(d); return; }
    if (d.kind === 'create') {
        // 끌지 않고 클릭이면 그 30분 눈금에서 1시간
        let st = d.st, et = d.et;
        if (!d.moved) { st = d.anchor - (d.anchor % 30); et = Math.min(DAY, st + 60); }
        state.swallowClick = d.moved;
        openTimedCreate(d.date, st, et);
        return;
    }
    const b = d.block;
    b.classList.remove('dragging');
    if (!d.moved) { if (d.armed) { restoreBlock(d); state.swallowClick = true; } return; } // 클릭 → click 핸들러가 패널을 연다
    state.swallowClick = true;
    const L = labels();
    if (d.kind === 'move') {
        if (d.curSt === d.origSt && d.curDate === d.origDate) { restoreBlock(d); return; }
        seriesAwarePatch(b,
            { start_date: b.dataset.start, end_date: b.dataset.end, start_time: b.dataset.stime, end_time: b.dataset.etime },
            { start_date: d.curDate, end_date: d.curDate, start_time: minToHHMM(d.curSt), end_time: minToHHMM(d.curEt) },
            L.moved);
    } else {
        if (d.curEt === d.origEt) { restoreBlock(d); return; }
        seriesAwarePatch(b, { end_time: b.dataset.etime }, { end_time: minToHHMM(d.curEt) }, L.resized || L.moved);
    }
}
function onCancel(e) {
    const p = state.pending;
    if (p && e.pointerId === p.d.pointerId) { clearTimeout(p.timer); state.pending = null; return; }
    if (state.drag && (state.drag.pointerId == null || e.pointerId === state.drag.pointerId)) cancelDrag();
}

function finishBand(d) {
    const b = d.bar;
    b.classList.remove('dragging');
    if (!d.moved) return; // click → 패널
    state.swallowClick = true;
    if (!d.delta) { restoreBand(d); return; }
    const L = labels();
    const s0 = b.dataset.start, e0 = b.dataset.end;
    if (d.kind === 'band-move') {
        seriesAwarePatch(b, { start_date: s0, end_date: e0 }, { start_date: addDays(s0, d.delta), end_date: addDays(e0, d.delta) }, L.moved);
    } else {
        seriesAwarePatch(b, { end_date: e0 }, { end_date: addDays(e0, d.delta) }, L.resized || L.moved);
    }
}
function restoreBand(d) {
    d.bar.style.setProperty('--col', String(d.col0));
    d.bar.style.setProperty('--span', String(d.span0));
}

function cancelDrag() {
    const d = state.drag;
    if (!d) return;
    state.drag = null;
    endDrag();
    hideSel();
    if (d.kind === 'band-move' || d.kind === 'band-resize') { d.bar.classList.remove('dragging'); restoreBand(d); }
    else if (d.kind !== 'create') { d.block.classList.remove('dragging'); restoreBlock(d); }
    state.swallowClick = d.moved;
}

function restoreBlock(d) {
    const b = d.block;
    b.style.setProperty('--top', String(d.origSt));
    b.style.setProperty('--h', String(d.origEt - d.origSt));
    if (b.parentElement !== d.origCol) d.origCol.appendChild(b);
    const t = b.querySelector('.cal-tg-block-time');
    if (t) t.textContent = d.origTimeText;
}

// ── 키보드 (BB3): 블록에 포커스 → ↑↓ 15분 · ←→ 하루 · Shift+↑↓ 끝 시각 · Esc 되돌림 ──
function onKey(e) {
    const block = e.target.closest && e.target.closest('.cal-tg-block');
    if (!block || e.target !== block || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'Escape' && state.kb && state.kb.block === block) { e.preventDefault(); e.stopPropagation(); cancelKb(); return; }
    const keys = { ArrowUp: [0, -SNAP], ArrowDown: [0, SNAP], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
    const k = keys[e.key];
    if (!k || !editable(block)) return;
    e.preventDefault();
    e.stopPropagation(); // 문서의 ←/→ 주 이동(calendar.js)보다 먼저 — 블록 위의 화살표는 블록의 것이다
    let kb = state.kb;
    if (!kb || kb.block !== block) {
        if (kb) flushKb();
        const [st, et] = timeRangeOf(block);
        const col = block.closest('.cal-tg-col');
        kb = state.kb = { block, id: block.dataset.id, origCol: col, origSt: st, origEt: et, origDate: col.dataset.date, curSt: st, curEt: et, curDate: col.dataset.date,
            origTimeText: block.querySelector('.cal-tg-block-time') ? block.querySelector('.cal-tg-block-time').textContent : '', timer: 0 };
    }
    if (e.shiftKey) {
        if (!k[1]) return; // Shift+←→ 는 없다
        kb.curEt = Math.max(kb.curSt + SNAP, Math.min(DAY, kb.curEt + k[1]));
        block.style.setProperty('--h', String(kb.curEt - kb.curSt));
    } else if (k[1]) {
        const dur = kb.curEt - kb.curSt;
        kb.curSt = Math.max(0, Math.min(DAY - dur, kb.curSt + k[1]));
        kb.curEt = kb.curSt + dur;
        block.style.setProperty('--top', String(kb.curSt));
    } else {
        const next = addDays(kb.curDate, k[0]);
        const col = state.root.querySelector('.cal-tg-col[data-date="' + next + '"]');
        if (!col) return; // 이 주 밖으로는 ‹ › 로 주를 옮긴 뒤에
        kb.curDate = next;
        col.appendChild(block);
        block.focus({ preventScroll: true });
        if (col.hidden) { state.first = parseInt(col.dataset.col, 10) || 0; applyCols(state.root, false); }
    }
    const txt = minToHHMM(kb.curSt) + '–' + minToHHMM(kb.curEt);
    const t = block.querySelector('.cal-tg-block-time');
    if (t) t.textContent = txt;
    announce(kb.curDate + ' ' + txt);
    clearTimeout(kb.timer);
    kb.timer = setTimeout(flushKb, KB_IDLE);
}
function flushKb() {
    const kb = state.kb;
    if (!kb) return;
    state.kb = null;
    clearTimeout(kb.timer);
    const b = kb.block;
    if (kb.curSt === kb.origSt && kb.curEt === kb.origEt && kb.curDate === kb.origDate) return;
    state.focusId = kb.id;
    const L = labels();
    seriesAwarePatch(b,
        { start_date: b.dataset.start, end_date: b.dataset.end, start_time: b.dataset.stime, end_time: b.dataset.etime },
        { start_date: kb.curDate, end_date: kb.curDate, start_time: minToHHMM(kb.curSt), end_time: minToHHMM(kb.curEt) },
        kb.curEt - kb.curSt !== kb.origEt - kb.origSt && kb.curSt === kb.origSt ? (L.resized || L.moved) : L.moved);
}
function cancelKb() {
    const kb = state.kb;
    if (!kb) return;
    state.kb = null;
    clearTimeout(kb.timer);
    restoreBlock(kb);
    kb.block.focus({ preventScroll: true });
}
function announce(text) {
    let live = state.root && state.root.querySelector('.cal-tg-live');
    if (!live && state.root) { live = document.createElement('div'); live.className = 'cal-tg-live sr-only'; live.setAttribute('aria-live', 'polite'); state.root.appendChild(live); }
    if (live) live.textContent = text;
}

// ── 「+N」 목록 (BB5) ──
let moreOpen = null;
function closeMore() {
    if (!moreOpen) return;
    const { btn, list } = moreOpen;
    moreOpen = null;
    list.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    closedExclusive(MORE_ID);
}
function toggleMore(btn) {
    const wrap = btn.closest('.cal-tg-more');
    const list = wrap && wrap.querySelector('.cal-tg-more-list');
    if (!list) return;
    if (moreOpen && moreOpen.btn === btn) { closeMore(); btn.focus(); return; }
    closeMore();
    list.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    moreOpen = { btn, list };
    openExclusive(MORE_ID, () => { if (moreOpen) { moreOpen.list.hidden = true; moreOpen.btn.setAttribute('aria-expanded', 'false'); moreOpen = null; } }, { roots: () => [wrap] });
    const first = list.querySelector('.cal-tg-more-item');
    if (first) first.focus({ preventScroll: true });
}
document.addEventListener('calendar:refreshed', () => { if (moreOpen && !moreOpen.btn.isConnected) { moreOpen = null; closedExclusive(MORE_ID); } });

// 반복 인스턴스는 범위를 묻고, 「이 일정만」만 실행 취소 스택에 (반복 스펙 §6.2, 월 그리드와 같은 규칙)
function seriesAwarePatch(block, before, after, label) {
    const id = block.dataset.id;
    if (!block.dataset.series) { commit(patchCommand(id, before, after), label); return; }
    const SL = state.root ? state.root.dataset : {};
    appChoose({
        title: SL.scopeTitle || '반복 일정을 어떻게 바꿀까요?',
        choices: [
            { value: 'this', label: SL.scopeThis || '이 일정만', primary: true },
            { value: 'following', label: SL.scopeFollowing || '이후 모두' },
            { value: 'all', label: SL.scopeAll || '전체' },
        ],
    }).then((scope) => {
        if (scope === null) { refreshGrid(); return; }
        if (scope === 'this') { commit(patchCommand(id, before, after), label); return; }
        api.patch(id, Object.assign({}, after, { series_scope: scope }))
            .then((r) => { showToast(fmtCount(SL.msgSeriesChanged || '일정 %d개를 바꿨습니다', ((r && r.affected) || 0) + 1)); return refreshGrid(); })
            .catch(() => { showToast(labels().failed, { error: true }); refreshGrid(); });
    });
}

// ── 유령 블록 (XA20): 터치 짧게 누르기의 「＋ 새 일정」 ──
function showGhost(col, date, st, et) {
    removeGhost();
    if (!col || !col.isConnected) return;
    const R = (state.root && state.root.dataset) || {};
    const label = R.ghostLabel || '＋ 새 일정';
    const g = document.createElement('button');
    g.type = 'button';
    g.className = 'cal-tg-ghost';
    g.dataset.day = date;
    g.dataset.start = minToHHMM(st);
    g.dataset.end = minToHHMM(et);
    g.style.setProperty('--top', String(st));
    g.style.setProperty('--h', String(et - st));
    const time = document.createElement('span');
    time.className = 'cal-tg-block-time';
    time.textContent = g.dataset.start + '–' + g.dataset.end;
    const name = document.createElement('span');
    name.className = 'cal-tg-ghost-label';
    name.textContent = label;
    g.append(time, name);
    g.setAttribute('aria-label', label + ' ' + time.textContent);
    col.appendChild(g);
    state.ghost = g;
}
function removeGhost() {
    const g = state.ghost;
    state.ghost = null;
    if (g && g.isConnected) g.remove();
}
function openFromGhost(g) {
    const d = g.dataset;
    removeGhost();
    openTimedCreate(d.day, hhmmToMin(d.start), hhmmToMin(d.end));
}

// ── 빈 시간 → 새 일정 패널을 시각 지정으로 ──
function openTimedCreate(date, st, et) {
    if (!window.openCalendarCreate) return;
    window.openCalendarCreate(date, date);
    // 허브의 시간표(BB10)는 그 프로젝트의 작업으로 — 타임라인의 「＋ 일정 추가」와 같은 방법
    const calId = state.root && state.root.dataset.calId;
    const calSel = document.getElementById('calCalSel');
    if (calId && calSel) { calSel.value = calId; calSel.dispatchEvent(new Event('change', { bubbles: true })); }
    const allDay = document.getElementById('calAllDay');
    if (allDay) allDay.checked = false;
    setTimeInput('calSTime', minToHHMM(st));
    setTimeInput('calETime', minToHHMM(Math.min(DAY - SNAP, et)));
    if (window.toggleCalendarTimes) window.toggleCalendarTimes();
}
function setTimeInput(id, v) {
    const sel = document.getElementById(id);
    if (!sel) return;
    sel.value = v;
    sel.setCustomValidity('');
    sel.removeAttribute('aria-invalid');
}

// ── 선택 범위 하이라이트 ──
function showSel(col, st, et) {
    let sel = col.querySelector('.cal-tg-sel');
    if (!sel) { sel = document.createElement('div'); sel.className = 'cal-tg-sel'; col.appendChild(sel); }
    sel.style.setProperty('--top', String(st));
    sel.style.setProperty('--h', String(et - st));
    sel.textContent = minToHHMM(st) + '–' + minToHHMM(et);
}
function hideSel() {
    document.querySelectorAll('.cal-tg-sel').forEach((el) => el.remove());
}
